import React, { useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Button, Card, CardHeader, Modal } from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError, toastSuccess } from "../../shared/toastHelper";

const ACTIVE_WINDOW_MINUTES = 5;

const lastSeenText = (lastSeenAt) => {
  if (!lastSeenAt) return "Not used yet";

  const seen = moment(lastSeenAt);
  if (moment().diff(seen, "minutes") < ACTIVE_WINDOW_MINUTES) return "Active now";
  return `Last seen ${seen.fromNow()}`;
};

const rowStyle = {
  display: "flex",
  gap: 12,
  alignItems: "center",
  padding: "12px 0",
  borderTop: "1px solid var(--sl-border-secondary)",
};

const TabletIcon = () => (
  <div
    style={{
      width: 36,
      height: 36,
      borderRadius: "var(--sl-radius-md)",
      border: "1px solid var(--sl-border-secondary)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "var(--sl-fg-quaternary)",
      flexShrink: 0,
    }}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M12 18h.01" />
    </svg>
  </div>
);

const KioskDevicesCard = ({ kioskDevices }) => {
  const [devices, setDevices] = useState(kioskDevices);
  const [signingOutId, setSigningOutId] = useState(null);
  const [showingSetup, setShowingSetup] = useState(false);

  const signOut = async (device) => {
    setSigningOutId(device.id);
    try {
      await client.delete(device.revokePath);
      setDevices((current) => current.filter((item) => item.id !== device.id));
      toastSuccess(`${device.name} signed out. A manager has to authorize it again.`);
    } catch (error) {
      toastError(`Could not sign out ${device.name}. Refresh the page and try again.`);
    } finally {
      setSigningOutId(null);
    }
  };

  return (
    <Card
      padding="md"
      header={
        <CardHeader
          title="Kiosk devices"
          supportingText="Shared tablets where staff clock in with their PIN."
          actions={
            <Button variant="secondary" onClick={() => setShowingSetup(true)}>
              Add a tablet
            </Button>
          }
        />
      }
    >
      {devices.length === 0 && (
        <div style={{ fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
          No tablets yet. Add one so staff can clock in with their PIN at the venue, or they can keep clocking in from their phones.
        </div>
      )}
      {devices.map((device, index) => (
        <div key={device.id} data-kiosk-device={device.id} style={index === 0 ? { ...rowStyle, borderTop: "none", paddingTop: 0 } : rowStyle}>
          <TabletIcon />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, color: "var(--sl-text-primary)" }}>{device.name}</span>
            <span style={{ fontSize: 13, lineHeight: "18px", color: "var(--sl-text-tertiary)" }}>
              {[device.venueName, lastSeenText(device.lastSeenAt), device.authorizedBy && `Authorized by ${device.authorizedBy}`].filter(Boolean).join(" · ")}
            </span>
          </div>
          <Button variant="link-destructive" disabled={signingOutId === device.id} onClick={() => signOut(device)}>
            Sign out
          </Button>
        </div>
      ))}
      {showingSetup && (
        <Modal
          open
          onClose={() => setShowingSetup(false)}
          title="Add a tablet"
          description="Set it up on the tablet itself. It stays signed in to one venue and only shows the clock-in screen."
          footer={
            <Button variant="primary" onClick={() => setShowingSetup(false)}>
              Done
            </Button>
          }
        >
          <ol style={{ margin: 0, paddingLeft: 20, display: "flex", flexDirection: "column", gap: 8, fontSize: 14, lineHeight: "20px", color: "var(--sl-text-secondary)" }}>
            <li>Install the Opendate Kiosk app from the App Store on the iPad.</li>
            <li>Open it and sign in with your Opendate account. You need Staffing settings access for the venue.</li>
            <li>Pick the venue (and the team, if you&rsquo;re on more than one) and name the tablet, for example &ldquo;Box office iPad&rdquo;.</li>
            <li>Tap Authorize this device. Refresh this page to see it here.</li>
          </ol>
        </Modal>
      )}
    </Card>
  );
};

KioskDevicesCard.propTypes = {
  kioskDevices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      venueName: PropTypes.string.isRequired,
      authorizedBy: PropTypes.string,
      lastSeenAt: PropTypes.string,
      revokePath: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default KioskDevicesCard;
